/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import {
  ChevronRight,
  FileText,
  Shield,
  Users,
  CreditCard,
  Phone,
  Mail,
  Check,
  Building,
  MapPin,
  AlertTriangle,
  Ban,
  RefreshCw,
  Copyright,
  Scale,
  X,
} from "lucide-react";

const TermsAndConditions = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [readProgress, setReadProgress] = useState(0);

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrolled / maxHeight) * 100;
      setReadProgress(progress);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const sections = [
    {
      id: "eligibility",
      title: "Eligibility",
      icon: <Shield className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed mb-4">
            To use EventPlanly, you must:
          </p>
          <ul className="space-y-3">
            {[
              "Be at least 18 years of age (as per Indian Contract Act, 1872).",
              "Provide accurate and complete registration details.",
              "Not be previously banned from using our services."
            ].map((item, index) => (
              <li key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: "responsibilities",
      title: "User Responsibilities",
      icon: <Users className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
            <h4 className="font-semibold text-lg mb-3 text-blue-800">
              As a user (customer):
            </h4>
            <ul className="space-y-2 text-blue-700">
              <li>• You agree to provide accurate booking details.</li>
              <li>• You are responsible for payments made via the platform.</li>
            </ul>
          </div>

          <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
            <h4 className="font-semibold text-lg mb-3 text-green-800">
              As a partner (service provider):
            </h4>
            <ul className="space-y-2 text-green-700">
              <li>• You must provide accurate service details, pricing, and availability.</li>
              <li>• You must comply with applicable licenses, permits, and regulations under Indian law.</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "booking",
      title: "Booking & Payments",
      icon: <CreditCard className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <ul className="space-y-3">
            {[
              "Users must make payments via secure methods provided in-app.",
              "EventPlanly deducts applicable service and convenience fees (see Privacy Policy).",
              "Bookings are confirmed only after payment acknowledgment."
            ].map((item, index) => (
              <li key={index} className="flex items-start space-x-3">
                <Check className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: "cancellation",
      title: "Cancellation & Refunds",
      icon: <RefreshCw className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            Cancellations and refunds will be handled as per the following rules:
          </p>
          <ul className="space-y-3">
            {[
              "Users must cancel within the partner's cancellation policy timelines.",
              "Refunds, if applicable, will exclude non-refundable convenience fees and will be processed within 72 working hours.",
              "EventPlanly reserves the right to mediate disputes but final responsibility lies with the user and partner."
            ].map((item, index) => (
              <li key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: "prohibited",
      title: "Prohibited Activities",
      icon: <Ban className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            You agree not to:
          </p>
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <ul className="space-y-2 text-red-700">
              <li>• Use the App for fraudulent or illegal activities.</li>
              <li>• Post offensive, defamatory, or misleading content.</li>
              <li>• Bypass payment channels or conduct transactions outside the App to avoid fees.</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: "intellectual",
      title: "Intellectual Property",
      icon: <Copyright className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
            <p className="text-purple-800">
              All logos, branding, content, and features of EventPlanly are the intellectual property of <span className="font-bold">ITDesks</span>. Unauthorized copying, distribution, or resale is strictly prohibited.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "liability",
      title: "Liability Disclaimer",
      icon: <AlertTriangle className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            EventPlanly acts as an intermediary between users and partners. We are not responsible for:
          </p>
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <ul className="space-y-2 text-yellow-800">
              <li>• The quality, safety, or legality of partner services.</li>
              <li>• Any disputes, damages, or losses between users and partners.</li>
            </ul>
          </div>
          <p className="text-gray-700">
            Our liability is limited to the service fees paid to EventPlanly, subject to applicable Indian laws including the Consumer Protection Act, 2019.
          </p>
        </div>
      ),
    },
    {
      id: "termination",
      title: "Termination",
      icon: <X className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            We may suspend or terminate your account if:
          </p>
          <ul className="space-y-3">
            {[
              "You violate these Terms.",
              "You engage in fraudulent, illegal, or harmful activity."
            ].map((item, index) => (
              <li key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      ),
    },
    {
      id: "governing",
      title: "Governing Law & Dispute Resolution",
      icon: <Scale className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
            <p className="text-indigo-800">
              These Terms are governed by the laws of India, including the Indian Contract Act, 1872, and Consumer Protection Act, 2019. Any disputes shall be subject to the exclusive jurisdiction of the courts of <span className="font-bold">Houston, Texas, USA</span> under our parent company <span className="font-bold">ITDesks</span>.
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "changes",
      title: "Changes to Terms",
      icon: <RefreshCw className="w-5 h-5" />,
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            We may update these Terms occasionally. Updates will be notified in-app or via email. Continued use of the App after updates implies acceptance.
          </p>
        </div>
      ),
    },
    {
      id: "contact",
      title: "Contact Us",
      icon: <Phone className="w-5 h-5" />,
      content: (
        <div className="space-y-6">
          <p className="text-gray-700 leading-relaxed">
            If you have questions regarding these Terms, contact:
          </p>
          
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white p-6 rounded-lg">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5" />
                <span className="font-semibold">info@itdesks.co</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5" />
                <span className="font-semibold">+1 (510) 520-7318</span>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin size={18} className="text-purple-600" />
                <span className="font-medium">Houston, TX, 77493, US</span>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const toggleSection = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Progress Bar */}
      <div
        className="fixed top-0 left-0 h-1 bg-gradient-to-r from-indigo-500 to-purple-600 z-50 transition-all duration-300"
        style={{ width: `${readProgress}%` }}
      />

      {/* Header */}
      <header className="bg-white shadow-lg border-b border-gray-200 animate-fadeInDown">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center">
            <div className="mb-4">
              <FileText className="w-16 h-16 text-indigo-600 mx-auto mb-4 animate-bounce" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2 animate-fadeInUp">
              EventPlanly — Terms & Conditions
            </h1>
            <div className="text-lg text-gray-600 mb-4 animate-fadeInUp" style={{animationDelay: '0.2s'}}>
              <p className="mb-2">
                <span className="font-semibold">Effective Date:</span> <span className="font-bold">October 21, 2025</span> · <span className="font-semibold">Last Updated:</span> <span className="font-bold">October 21, 2025</span>
              </p>
              <p className="text-base">
                These Terms & Conditions ("Terms") govern your use of EventPlanly ("App", "we", "our", "us"). By accessing or using the App, you agree to comply with these Terms. If you do not agree, please refrain from using the App.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden animate-fadeInUp" style={{animationDelay: '0.4s'}}>
          <div className="divide-y divide-gray-200">
            {sections.map((section, index) => (
              <div
                key={section.id}
                className="border-b border-gray-100 last:border-b-0 animate-slideInLeft"
                style={{animationDelay: `${(index * 0.1) + 0.6}s`}}
              >
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full px-6 py-6 text-left hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-white hover:scale-110 transition-transform duration-200">
                        {section.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {index + 1}. {section.title}
                        </h3>
                      </div>
                    </div>
                    <div 
                      className={`transform transition-transform duration-200 ${
                        activeSection === section.id ? 'rotate-90' : 'rotate-0'
                      }`}
                    >
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </button>

                {activeSection === section.id && (
                  <div className="px-6 pb-6 animate-fadeIn">
                    {section.content}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Last Updated */}
        <div className="text-center py-8 animate-fadeInUp" style={{animationDelay: '1s'}}>
          <div className="inline-flex items-center gap-2 bg-white/50 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <p className="text-slate-600 text-sm">
              Last updated:{" "}
              <span className="font-medium">October 21, 2025</span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center animate-fadeIn" style={{animationDelay: '1.2s'}}>
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-lg">
            <p className="text-xl font-bold mb-2">
              © EventPlanly — A Child Company of ITdesks. All rights reserved.
            </p>
          </div>
        </footer>
      </main>

      <style jsx>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeInDown {
          animation: fadeInDown 0.6s ease-out forwards;
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        .animate-slideInLeft {
          animation: slideInLeft 0.5s ease-out forwards;
          opacity: 0;
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default TermsAndConditions;